import { useGetUsersWithAnyOfPermissions } from "@/spz-api/companies/companies";
import {
  type GetCompanyUsersResponseDto,
  GetUsersWithAnyOfPermissionsPermissionIdsItem,
} from "@/spz-api/model";
import type { RichText } from "@spz/shared-domain";
import type { TextEditorHandle } from "@spz/ui/components/text-editor";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type CommentFieldValue = {
  content: RichText;
  attachment: File | null;
};

const mapCompanyUsersToMentionItems = (
  response: GetCompanyUsersResponseDto | undefined,
) => {
  if (!response) {
    return [];
  }

  return response.data
    .sort((a, b) =>
      (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName),
    )
    .map((user) => ({
      id: user.id.toString(),
      name: `${user.firstName} ${user.lastName}`,
      description: user.email,
    }));
};

const DEFAULT_CONTENT: RichText = {
  content: [],
  type: "doc",
} as const;

const useContentFormField = ({
  initialValue,
}: {
  initialValue: CommentFieldValue["content"] | undefined;
}) => {
  const [comment, setComment] = useState<RichText>(
    initialValue ?? DEFAULT_CONTENT,
  );

  return {
    update: (_comment: RichText) => {
      setComment(_comment);
    },
    reset: () => {
      setComment(DEFAULT_CONTENT);
      return undefined;
    },
    value: comment,
  };
};

const useAttachmentFormField = ({
  initialValue,
  allowedFileTypes,
}: {
  initialValue: CommentFieldValue["attachment"] | undefined;
  allowedFileTypes?: string[];
}) => {
  const [attachment, setAttachment] = useState<File | null>(
    initialValue ?? null,
  );

  return {
    allowedFileTypes: allowedFileTypes ?? [],
    update: (_attachment: File) => {
      setAttachment(_attachment);
    },
    reset: () => {
      setAttachment(null);
      return null;
    },
    value: attachment,
  };
};

export const useCommentFieldViewModel = ({
  companyId,
  initialValues,
  allowedFileTypes,
  onChange,
  onBlur,
}: {
  companyId: string;
  initialValues?: Partial<CommentFieldValue>;
  allowedFileTypes?: string[];
  onChange: (values: CommentFieldValue) => void;
  onBlur?: () => void;
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<TextEditorHandle | null>(null);

  const mentionUsers = useGetUsersWithAnyOfPermissions(companyId, {
    permissionIds: [
      GetUsersWithAnyOfPermissionsPermissionIdsItem["spz:control:view"],
    ],
  });
  const mentionableItems = mapCompanyUsersToMentionItems(mentionUsers.data);

  const commentFormField = useContentFormField({
    initialValue: initialValues?.content,
  });
  const attachmentFormField = useAttachmentFormField({
    initialValue: initialValues?.attachment,
    allowedFileTypes,
  });

  const formEmitChanges = (values?: Partial<CommentFieldValue>) => {
    onChange({
      content: commentFormField.value,
      attachment: attachmentFormField.value,
      ...values,
    });
    onBlur?.();
  };

  const form = {
    fields: {
      comment: commentFormField,
      attachment: attachmentFormField,
    },
    update: (values: Partial<CommentFieldValue>) => {
      if ("content" in values) {
        if (values.content) {
          commentFormField.update(values.content);
        } else {
          commentFormField.reset();
        }
      }

      if ("attachment" in values) {
        if (values.attachment) {
          attachmentFormField.update(values.attachment);
        } else {
          attachmentFormField.reset();
        }
      }

      // Emit with the new values immediately, don't wait for state update
      formEmitChanges(values);
    },
    emit: () => formEmitChanges(),
    reset: () => {
      commentFormField.reset();
      attachmentFormField.reset();
    },
  };

  return {
    t,
    editorRef,
    form: {
      fields: {
        comment: commentFormField,
        attachment: attachmentFormField,
      },
      update: (values: Partial<CommentFieldValue>) => {
        // Build the new values to emit
        const newValues: Partial<CommentFieldValue> = {};

        if ("content" in values) {
          if (values.content) {
            commentFormField.update(values.content);
            newValues.content = values.content;
          } else {
            newValues.content = commentFormField.reset();
          }
        }

        if ("attachment" in values) {
          if (values.attachment) {
            attachmentFormField.update(values.attachment);
            newValues.attachment = values.attachment;
          } else {
            newValues.attachment = attachmentFormField.reset();
          }
        }

        // Emit with the new values immediately, don't wait for state update
        formEmitChanges(newValues);
      },
      emit: () => formEmitChanges(),
    },
    isMentionLoading: mentionUsers.isPending,
    mentions: {
      items: mentionableItems,
      loading: mentionUsers.isPending,
    },
    reset: () => {
      editorRef.current?.reset();
      form.reset();
    },
  };
};
